// In-app viewer for the per-role user manuals (2026-07-08, George: "where do
// I find the Manuals on the CRM?" -- they only existed as repo files + emailed
// .docx before this). Tabs across all five roles -- any authenticated user can
// read any manual (plain how-to content, no live figures). Opens on the tab
// matching the caller's own role by default.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { styles, colors, useIsMobile } from '../components/UI';
import MiniMarkdown from '../components/MiniMarkdown';

export default function Manuals() {
  // ?m=<key> lets other pages deep-link straight to their own manual
  // (Operations -> ISNetworld does this). Falls back to the server default.
  const [searchParams] = useSearchParams();
  const wanted = searchParams.get('m');
  const [list, setList] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [content, setContent] = useState(null);
  const [error, setError] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.listManuals()
      .then(d => {
        setList(d.manuals);
        const match = wanted && d.manuals.some(m => m.key === wanted) ? wanted : d.default_key;
        setActiveKey(match);
      })
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    if (!activeKey) return;
    setLoadingContent(true);
    setError('');
    api.getManual(activeKey)
      .then(d => setContent(d.content))
      .catch(e => setError(e.message))
      .finally(() => setLoadingContent(false));
  }, [activeKey]);

  // Version from docs/manuals/manuals.version.json (fleet manual contract).
  const activeMeta = list && list.find(m => m.key === activeKey);

  if (error && !list) return <div style={styles.errorBox}>{error}</div>;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <h1 style={{ ...styles.h1, margin: 0 }}>
          📖 User Manuals <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 14 }}>· all roles</span>
        </h1>
        {activeMeta?.version && (
          <span style={{ color: colors.textMuted, fontSize: 13, fontWeight: 600 }}>
            {activeMeta.label} v{activeMeta.version}{activeMeta.updated ? ` (${activeMeta.updated})` : ''}
          </span>
        )}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {list && (
        <div style={{
          display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap',
          borderBottom: `1px solid ${colors.border}`, paddingBottom: 12,
        }}>
          {list.map(m => {
            const active = activeKey === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setActiveKey(m.key)}
                style={{
                  padding: isMobile ? '8px 12px' : '7px 16px',
                  borderRadius: 8, border: `1.5px solid ${active ? colors.orange : colors.border}`,
                  background: active ? colors.orange : '#fff',
                  color: active ? '#fff' : '#374151',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={{
        ...styles.card, maxWidth: 900, margin: '0 auto',
        padding: isMobile ? '18px 16px' : '32px 40px',
      }}>
        {loadingContent && <div style={{ color: colors.textMuted, padding: 20 }}>Loading manual…</div>}
        {!loadingContent && content && <MiniMarkdown source={content} />}
      </div>
    </>
  );
}
